import React, { useState, useMemo, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { useDashboardSummary, useTimeSeries, usePerformanceReport } from "@/hooks/use-reporting";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/utils";
import { Download } from "lucide-react";
import { api } from "@/lib/api";

type Granularity = "hour" | "day" | "week" | "month";

function getDefaultDates() {
  const to = new Date();
  to.setDate(to.getDate() + 1);
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().split("T")[0],
    to: to.toISOString().split("T")[0],
  };
}

interface PerformanceRow {
  offer_id: number;
  affiliate_id: number;
  advertiser_id: number;
  country_code: string;
  clicks: number;
  unique_clicks: number;
  conversions: number;
  revenue: number;
  payout: number;
  profit: number;
  cr: number;
  epc: number;
}

const perfColumnHelper = createColumnHelper<PerformanceRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const perfColumns: ColumnDef<PerformanceRow, any>[] = [
  perfColumnHelper.accessor("offer_id", {
    header: "Offer",
    cell: (info) => <span className="font-mono text-xs">#{info.getValue()}</span>,
  }),
  perfColumnHelper.accessor("affiliate_id", {
    header: "Affiliate",
    cell: (info) => <span className="font-mono text-xs">#{info.getValue()}</span>,
  }),
  perfColumnHelper.accessor("advertiser_id", {
    header: "Advertiser",
    cell: (info) => <span className="font-mono text-xs">#{info.getValue()}</span>,
  }),
  perfColumnHelper.accessor("country_code", {
    header: "Country",
    cell: (info) => <span className="text-gray-700">{info.getValue() || "-"}</span>,
  }),
  perfColumnHelper.accessor("clicks", {
    header: "Clicks",
    cell: (info) => formatNumber(info.getValue()),
  }),
  perfColumnHelper.accessor("unique_clicks", {
    header: "Uniques",
    cell: (info) => formatNumber(info.getValue()),
  }),
  perfColumnHelper.accessor("conversions", {
    header: "Conv.",
    cell: (info) => formatNumber(info.getValue()),
  }),
  perfColumnHelper.accessor("cr", {
    header: "CR",
    cell: (info) => formatPercent(info.getValue()),
  }),
  perfColumnHelper.accessor("revenue", {
    header: "Revenue",
    cell: (info) => <span className="font-medium">{formatCurrency(info.getValue())}</span>,
  }),
  perfColumnHelper.accessor("payout", {
    header: "Payout",
    cell: (info) => formatCurrency(info.getValue()),
  }),
  perfColumnHelper.accessor("profit", {
    header: "Profit",
    cell: (info) => {
      const val = info.getValue();
      return (
        <span className={val >= 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
          {formatCurrency(val)}
        </span>
      );
    },
  }),
];

export function ReportingPage() {
  const defaults = useMemo(() => getDefaultDates(), []);
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [granularity, setGranularity] = useState<Granularity>("day");

  const { data: summaryData, error: summaryError } = useDashboardSummary(dateFrom, dateTo);
  const { data: tsData, isLoading: tsLoading, error: tsError } = useTimeSeries(dateFrom, dateTo, granularity);
  const { data: perfData, isLoading: perfLoading, error: perfError } = usePerformanceReport({
    from: dateFrom,
    to: dateTo,
    columns: ["offer_id", "affiliate_id", "advertiser_id", "country_code"],
  });

  const chartData = useMemo(() => {
    if (!tsData?.data) return [];
    return (tsData.data as Array<{ period: string; clicks: number; conversions: number; revenue: number }>).map((d) => ({
      period: d.period,
      clicks: Number(d.clicks) || 0,
      conversions: Number(d.conversions) || 0,
      revenue: Number(d.revenue) || 0,
    }));
  }, [tsData]);

  const perfRows = useMemo(() => {
    if (!perfData?.data) return [];
    return (perfData.data as Array<Record<string, number | string>>).map((row) => {
      const clicks = Number(row.clicks) || 0;
      const conversions = Number(row.conversions) || 0;
      const revenue = Number(row.revenue) || 0;
      const payout = Number(row.payout) || 0;
      return {
        offer_id: Number(row.offer_id),
        affiliate_id: Number(row.affiliate_id),
        advertiser_id: Number(row.advertiser_id),
        country_code: String(row.country_code || ""),
        clicks,
        unique_clicks: Number(row.unique_clicks) || 0,
        conversions,
        revenue,
        payout,
        profit: revenue - payout,
        cr: clicks > 0 ? (conversions / clicks) * 100 : 0,
        epc: clicks > 0 ? payout / clicks : 0,
      } satisfies PerformanceRow;
    });
  }, [perfData]);

  return (
    <div>
      <PageHeader
        title="Reporting"
        description="Analyze your performance metrics"
      />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        <select
          value={granularity}
          onChange={(e) => setGranularity(e.target.value as Granularity)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="hour">Hourly</option>
          <option value="day">Daily</option>
          <option value="week">Weekly</option>
          <option value="month">Monthly</option>
        </select>
      </div>

      {(summaryError || tsError || perfError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-700 font-medium">Error loading reports</p>
          <p className="text-xs text-red-600 mt-1">
            {(summaryError as Error)?.message || (tsError as Error)?.message || (perfError as Error)?.message}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
        {[
          { label: "Clicks", value: formatNumber(summaryData?.data?.clicks ?? 0), sub: `${formatNumber(summaryData?.data?.uniqueClicks ?? 0)} unique` },
          { label: "Conversions", value: formatNumber(summaryData?.data?.conversions ?? 0), sub: `${formatPercent(summaryData?.data?.cr ?? 0)} CR` },
          { label: "Revenue", value: formatCurrency(summaryData?.data?.revenue ?? 0) },
          { label: "Payout", value: formatCurrency(summaryData?.data?.payout ?? 0) },
          { label: "Profit", value: formatCurrency(summaryData?.data?.profit ?? 0), highlight: true },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{kpi.label}</p>
            <p className={`text-2xl font-bold mt-1 ${kpi.highlight ? ((summaryData?.data?.profit ?? 0) >= 0 ? "text-green-600" : "text-red-600") : "text-gray-900"}`}>
              {kpi.value}
            </p>
            {kpi.sub && <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <Card title="Clicks & Conversions Over Time">
          {tsLoading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-gray-500 text-sm">
              No data available for the selected period
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val: string) => {
                      if (granularity === "hour") return val.slice(11, 16);
                      return val.slice(5, 10);
                    }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="clicks"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                    name="Clicks"
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="conversions"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    name="Conversions"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    name="Revenue ($)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Performance Breakdown</h2>
            <button onClick={async () => {
              const res = await api.post("/v1/reporting/performance/export", {
                from: dateFrom, to: dateTo,
                columns: ["offer_id", "affiliate_id", "advertiser_id", "country_code"],
              });
              if (!res.ok) return;
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "performance_report.csv"; a.click();
              URL.revokeObjectURL(url);
            }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
          <DataTable
            columns={perfColumns}
            data={perfRows}
            isLoading={perfLoading}
          />
        </div>
      </div>
    </div>
  );
}
